import { useContext, useEffect, useRef, useState } from "react";

import { cn } from "utils";
import { MatchContext, SearchContext } from "context/providers";
import ImageViewer from "components/ImageViewer";
import CloudinaryWidget from "components/CloudinaryWidget";

import placeholderPicture from "images/profile-dummy-photo.png";

const ProfileImage = ({ hit }) => {
  const { headshotKey, id: hitId, match: hitMatch } = hit;

  const { updateMatchUserFieldLocally, matches } = useContext(MatchContext);
  const { updateHit } = useContext(SearchContext);

  const [updatingProfilePicture, setUpdatingProfilePicture] = useState(0);

  const newKeyRef = useRef(null);

  const saveAttributes = async (attributes) => {
    newKeyRef.current = attributes.headshotKey;

    setUpdatingProfilePicture(1);

    const attributesToUpdate = {
      input: {
        id: hitId,
        ...attributes,
      },
    };

    try {
      let applicationId;

      if (matches && matches.length > 0) {
        applicationId =
          hitMatch?.applicationId ||
          matches.find(({ userId }) => userId === hit.id)?.applicationId;
      }

      if (applicationId) {
        updateMatchUserFieldLocally(
          applicationId,
          "headshotKey",
          attributes.headshotKey
        );
      }

      setTimeout(async () => {
        await updateHit(attributesToUpdate);

        setUpdatingProfilePicture(2);
      }, 100);
    } catch (error) {
      console.log("Error when updating hit", error);

      setUpdatingProfilePicture(0);
    }
  };

  useEffect(() => {
    if (updatingProfilePicture === 2 && newKeyRef.current === headshotKey) {
      newKeyRef.current = null;
      setTimeout(() => {
        setUpdatingProfilePicture(0);
      }, 1500);
    }
  }, [updatingProfilePicture, headshotKey]);

  return (
    <div className="relative">
      <div
        className={cn(
          "flex items-center overflow-hidden rounded-lg h-[81px] transition-all",
          {
            "animate-pulse": updatingProfilePicture > 0,
          }
        )}
      >
        <ImageViewer
          objectKey={headshotKey}
          width={81}
          responsive={false}
          placeholder={
            <img
              src={placeholderPicture}
              alt="placeholder"
              className="w-[81px] h-[81px]"
            />
          }
        />
      </div>
      {updatingProfilePicture === 0 && (
        <div className="flex justify-center items-center h-full w-full absolute top-0 left-0 transition-all duration-500 bg-white opacity-0 hover:opacity-90">
          <CloudinaryWidget
            onFinishAction={(cloudinaryPublicId, remove) => {
              if (!cloudinaryPublicId && !remove) {
                return;
              }
              saveAttributes({ headshotKey: cloudinaryPublicId });
            }}
            identifier="profile-pic-upload"
            uploadButtonClassName="justify-center"
            minImageWidth={317}
            minImageHeight={317}
            imageDimension="317 pixels wide by 317 pixels tall"
            resourceType="image"
            showWarningModal={headshotKey}
            warningModalMessage={
              <>
                <br />
                <span>
                  Are you sure you want to upload profile picture for this user?
                </span>
                <br />
                <span>
                  Uploading a photo will replace current profile picture.
                </span>
              </>
            }
            allowDelete={headshotKey}
            deleteModalMessage={
              <>
                <br />
                <span>
                  Are you sure you want to remove the profile picture for this
                  user?
                </span>
              </>
            }
          />
        </div>
      )}
    </div>
  );
};

export default ProfileImage;
